/**
 * Image Node Renderer
 *
 * Renders image-type nodes where the entire node is a picture with:
 * - Optional title (shown at top)
 * - Optional caption (shown at bottom)
 * - Labels can be shown inside (overlay) or outside the image
 * - Labels shown only on hover by default
 *
 * LOD levels:
 * - full: Full image with optional title/caption
 * - title: Smaller image with title overlay
 * - icon: Thumbnail representation
 * - dot: Colored dot
 */

import type { UINode, ScreenMetrics, WorldRect } from "../types";
import type {
  NodeRenderer,
  RenderLayers,
  RenderResult,
  LODLevel,
} from "../renderer";
import { calculateLOD } from "../renderer";
import { hashContent } from "../texture-cache";

/** Default LOD threshold below which we use textures (in screen pixels) */
const DEFAULT_TEXTURE_THRESHOLD = 60;

/** Image node data interface */
export interface ImageNodeData {
  /** Image URL (required) */
  src: string;
  /** Optional title shown at top */
  title?: string;
  /** Optional caption shown at bottom */
  caption?: string;
  /** Where to position labels: 'inside' overlays on image, 'outside' positions above/below */
  labelPosition?: "inside" | "outside";
  /** When to show labels: 'always' or 'hover' (default) */
  showLabels?: "always" | "hover";
  /** Alt text for accessibility */
  alt?: string;
  /** How the image fits the node: 'cover' (default), 'contain', or 'fill' */
  objectFit?: "cover" | "contain" | "fill";
}

/** Inline onerror handler that shows the error placeholder when an image fails to load */
const IMG_ONERROR = `this.style.display='none';this.parentElement.querySelector('.image-error').style.display=''`;

/**
 * Image renderer with hover-based label visibility and LOD support.
 */
export class ImageRenderer implements NodeRenderer {
  private lastLOD: Map<string, LODLevel> = new Map();
  private lastContent: Map<string, string> = new Map();
  private contentHashes: Map<string, string> = new Map();

  render(
    node: UINode,
    metrics: ScreenMetrics,
    layers: RenderLayers,
  ): RenderResult {
    const screenSize = Math.max(metrics.width, metrics.height);
    const opts = layers.options;

    // Calculate LOD based on screen size
    // forceLod takes precedence over everything
    const lod: LODLevel = opts.forceLod
      ? opts.forceLod
      : opts.simplifyLOD
        ? calculateLOD(metrics, {
            enableLodFull: opts.enableLodFull,
            enableLodTitle: opts.enableLodTitle,
            enableLodIcon: opts.enableLodIcon,
            enableLodDot: opts.enableLodDot,
          })
        : "full";

    // Calculate content hash for texture cache invalidation
    const baseContentHash = this.getContentHash(node);
    const contentHash = `${baseContentHash}:${lod}`;

    // Compound texture ID includes LOD level
    const textureId = `${node.id}:${lod}`;

    // Determine if we should use texture rendering
    // Don't use texture mode at 'dot' LOD - there's no image to cache, just a simple gradient
    // that renders fine in HTML and avoids potential issues with CSS capture
    const textureThreshold = opts.textureThreshold ?? DEFAULT_TEXTURE_THRESHOLD;

    // Check if the image URL is cross-origin - if so, SnapDOM won't be able to capture it
    // due to CORS restrictions. Keep these nodes in HTML rendering mode.
    const data = this.getData(node);
    const isCrossOrigin = data.src ? this.isCrossOriginUrl(data.src) : false;

    // forceTextureRendering forces texture mode regardless of screen size
    const wantTexture =
      (opts.forceTextureRendering || screenSize < textureThreshold) &&
      layers.webgl !== null &&
      lod !== "dot" &&
      !isCrossOrigin;

    // Check if we have a valid cached texture
    const hasValidTexture =
      wantTexture && layers.webgl?.hasValidTexture(textureId, contentHash);

    const lastLod = this.lastLOD.get(node.id);
    const lodChanged = lastLod !== lod;

    // Check if this is a newly created element
    const isNewElement = layers.html.isNewlyCreated(node.id);

    // Set data attribute for CSS styling
    layers.html.setDataAttribute(node.id, "lod", lod);

    // Try texture rendering if we have a valid texture
    if (hasValidTexture && layers.webgl) {
      const worldRect: WorldRect = {
        x: node.position.x,
        y: node.position.y,
        width: node.size.width,
        height: node.size.height,
      };

      // Rotation is applied via shader (texture is captured without rotation for efficiency)
      const textureDrawn = layers.webgl.drawCachedTexture(
        textureId,
        worldRect,
        1,
        node.rotation ?? 0,
      );

      if (textureDrawn) {
        layers.html.setContent(node.id, "");
        layers.html.addClass(node.id, "texture-cached");
        this.lastContent.delete(node.id);
        this.lastLOD.set(node.id, lod);
        return { lod, changed: lodChanged };
      }
    }

    // Fall back to HTML rendering
    layers.html.removeClass(node.id, "texture-cached");

    // Render content based on LOD
    const content = this.renderContent(node, lod);
    const lastContentValue = this.lastContent.get(node.id);
    const contentChanged = lastContentValue !== content || isNewElement;

    if (contentChanged) {
      // If the only change is the image src, update the <img> element directly
      // to avoid destroying already-loaded images (which causes a white flash).
      const srcUpdated =
        !isNewElement &&
        lastContentValue &&
        this.tryUpdateImgSrc(layers, node.id, lastContentValue, content);

      if (!srcUpdated) {
        layers.html.setContent(node.id, content);
      }
      this.lastContent.set(node.id, content);
    }

    // Request texture generation if needed
    if (wantTexture && layers.webgl && !hasValidTexture) {
      layers.webgl.requestTexture(
        textureId,
        () => layers.html.getContainer(node.id),
        contentHash,
        1,
        lod,
      );
    }

    this.lastLOD.set(node.id, lod);

    return { lod, changed: lodChanged || contentChanged };
  }

  /**
   * Try to update just the <img> src attribute instead of replacing innerHTML.
   * Returns true if the update was done, false if a full replacement is needed.
   */
  private tryUpdateImgSrc(
    layers: RenderLayers,
    nodeId: string,
    oldContent: string,
    newContent: string,
  ): boolean {
    // Extract src from both old and new content
    const srcRegex = /src="([^"]*)"/;
    const oldMatch = oldContent.match(srcRegex);
    const newMatch = newContent.match(srcRegex);
    if (!oldMatch || !newMatch) return false;

    // Check that everything except the src is identical
    const oldWithoutSrc = oldContent.replace(srcRegex, 'src=""');
    const newWithoutSrc = newContent.replace(srcRegex, 'src=""');
    if (oldWithoutSrc !== newWithoutSrc) return false;

    // The only difference is the src — update the <img> element directly
    const container = layers.html.getContainer(nodeId);
    if (!container) return false;

    const img = container.querySelector(
      "img.image-content",
    ) as HTMLImageElement | null;
    if (!img) return false;

    img.src = newMatch[1];
    return true;
  }

  /**
   * Get a content hash for cache invalidation.
   * Excludes non-serializable properties like _originalBlob.
   */
  private getContentHash(node: UINode): string {
    // Build a stable content string excluding blob references
    const data = node.data as Record<string, unknown>;
    const hashableData: Record<string, unknown> = {};
    for (const key of Object.keys(data)) {
      // Skip blob/non-serializable internal properties
      if (key.startsWith("_")) continue;
      hashableData[key] = data[key];
    }

    const contentStr = JSON.stringify(hashableData);
    const hash = hashContent(contentStr);

    // Only cache if it hasn't changed (for stable hashing)
    const cached = this.contentHashes.get(node.id);
    if (cached !== hash) {
      this.contentHashes.set(node.id, hash);
    }

    return hash;
  }

  private getData(node: UINode): ImageNodeData {
    return node.data as unknown as ImageNodeData;
  }

  private renderContent(node: UINode, lod: LODLevel): string {
    const data = this.getData(node);
    const src = data.src || "";
    const title = data.title || "";
    const caption = data.caption || "";
    const labelPosition = data.labelPosition || "inside";
    const showLabels = data.showLabels || "hover";
    const alt = data.alt || title || "Image";
    const objectFit = data.objectFit || "cover";

    const hasTitle = title.length > 0;
    const hasCaption = caption.length > 0;
    const hasLabels = hasTitle || hasCaption;

    // CSS classes for label visibility
    const labelVisibilityClass =
      showLabels === "hover" ? "labels-on-hover" : "labels-always";
    const labelPositionClass =
      labelPosition === "inside" ? "labels-inside" : "labels-outside";

    switch (lod) {
      case "full":
        return this.renderFull(
          src,
          title,
          caption,
          alt,
          objectFit,
          hasLabels,
          labelVisibilityClass,
          labelPositionClass,
        );

      case "title":
        return this.renderTitle(
          src,
          title,
          alt,
          objectFit,
          labelVisibilityClass,
        );

      case "icon":
        return this.renderIcon(src, alt);

      case "dot":
        return `<div class="image-node image-dot"></div>`;
    }
  }

  private renderFull(
    src: string,
    title: string,
    caption: string,
    alt: string,
    objectFit: string,
    hasLabels: boolean,
    labelVisibilityClass: string,
    labelPositionClass: string,
  ): string {
    const titleHtml = title
      ? `<div class="image-title">${this.escapeHtml(title)}</div>`
      : "";
    const captionHtml = caption
      ? `<div class="image-caption">${this.escapeHtml(caption)}</div>`
      : "";

    const labelsClass = hasLabels
      ? `has-labels ${labelVisibilityClass} ${labelPositionClass}`
      : "";

    // If src is empty/missing, show error state directly
    if (!src) {
      return `
				<div class="node-close-btn" data-action="close">&times;</div>
				<div class="image-node image-full ${labelsClass}">
					<div class="image-error">
						<span class="image-error-msg">Image could not be loaded</span>
					</div>
					${titleHtml}
					${captionHtml}
				</div>
			`;
    }

    return `
			<div class="node-close-btn" data-action="close">&times;</div>
			<div class="image-node image-full ${labelsClass}">
				<img
					src="${this.escapeHtml(src)}"
					alt="${this.escapeHtml(alt)}"
					class="image-content"
					style="object-fit: ${objectFit};"
					loading="lazy"
					draggable="false"
					onerror="${IMG_ONERROR}"
				/>
				<div class="image-error" style="display: none;">
					<span class="image-error-msg">Image could not be loaded</span>
				</div>
				${titleHtml}
				${captionHtml}
			</div>
		`;
  }

  private renderTitle(
    src: string,
    title: string,
    alt: string,
    objectFit: string,
    labelVisibilityClass: string,
  ): string {
    const titleHtml = title
      ? `<div class="image-title">${this.escapeHtml(title)}</div>`
      : "";

    const labelsClass = title
      ? `has-labels ${labelVisibilityClass} labels-inside`
      : "";

    // If src is empty/missing, show error state directly
    if (!src) {
      return `
				<div class="image-node image-title-lod ${labelsClass}">
					<div class="image-error">
						<span class="image-error-msg">Image could not be loaded</span>
					</div>
					${titleHtml}
				</div>
			`;
    }

    // Note: No loading="lazy" here - these LOD levels are captured as textures
    // and need images to be loaded immediately for proper capture
    return `
			<div class="image-node image-title-lod ${labelsClass}">
				<img
					src="${this.escapeHtml(src)}"
					alt="${this.escapeHtml(alt)}"
					class="image-content"
					style="object-fit: ${objectFit};"
					draggable="false"
					onerror="${IMG_ONERROR}"
				/>
				<div class="image-error" style="display: none;">
					<span class="image-error-msg">Image could not be loaded</span>
				</div>
				${titleHtml}
			</div>
		`;
  }

  private renderIcon(src: string, alt: string): string {
    // If src is empty/missing, show error state directly
    if (!src) {
      return `
				<div class="image-node image-icon-only">
					<div class="image-error image-error-compact">
						<span class="image-error-msg">Image could not be loaded</span>
					</div>
				</div>
			`;
    }

    // Note: No loading="lazy" here - icon LOD is captured as texture
    // and needs images to be loaded immediately for proper capture
    return `
			<div class="image-node image-icon-only">
				<img
					src="${this.escapeHtml(src)}"
					alt="${this.escapeHtml(alt)}"
					class="image-thumbnail"
					draggable="false"
					onerror="${IMG_ONERROR}"
				/>
				<div class="image-error image-error-compact" style="display: none;">
					<span class="image-error-msg">Image could not be loaded</span>
				</div>
			</div>
		`;
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  getPreferredSize(node: UINode): { width: number; height: number } {
    return {
      width: node.size?.width ?? 200,
      height: node.size?.height ?? 150,
    };
  }

  /**
   * Check if a URL is cross-origin (external).
   * Cross-origin images can be displayed in HTML but cannot be captured by SnapDOM
   * due to CORS restrictions - the browser blocks reading pixel data from external images.
   */
  private isCrossOriginUrl(url: string): boolean {
    // Data URLs and blob URLs are same-origin
    if (url.startsWith("data:") || url.startsWith("blob:")) {
      return false;
    }

    // Relative URLs are same-origin
    if (
      !url.startsWith("http://") &&
      !url.startsWith("https://") &&
      !url.startsWith("//")
    ) {
      return false;
    }

    // Check if the URL's origin matches the current page origin
    try {
      const urlObj = new URL(url, window.location.href);
      return urlObj.origin !== window.location.origin;
    } catch {
      // If URL parsing fails, assume it's same-origin (relative path)
      return false;
    }
  }

  cleanup(nodeId: string): void {
    this.lastLOD.delete(nodeId);
    this.lastContent.delete(nodeId);
    this.contentHashes.delete(nodeId);
  }
}
